import { EditOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { Badge, Button, Tag, Tooltip, Typography } from 'antd';
import React from 'react';
import {
  ORDER_STATUS,
  RETURN_ORDER_ITEM_STATUS,
} from '../../../../../../common/constants';
import { humanizeEnum } from '../../../../../../common/utils';
import { isSlotOrdered } from '../../../../ultraPlanUtils';
import ToyAgeTags from './ToyAgeTags';
import ToyThumbnail from './ToyThumbnail';

const { Text, Paragraph } = Typography;

const RETURN_STATUS_COLOR = '#fa8c16';

const getSlotOrderRows = (slot) => {
  const rows = [];
  if (slot?.isOrderPlaced || slot?.orderNumber || slot?.orderStatus) {
    rows.push({
      key: 'order',
      number: slot?.orderNumber,
      status: slot?.orderStatus,
      statusText:
        ORDER_STATUS?.[slot?.orderStatus]?.text ||
        humanizeEnum(slot?.orderStatus),
      statusColor: ORDER_STATUS?.[slot?.orderStatus]?.color,
      fallbackText: 'Order placed',
    });
  }
  if (slot?.isReturned || slot?.returnOrderNumber || slot?.returnOrderStatus) {
    const status = slot?.returnOrderStatus;
    rows.push({
      key: 'return',
      number: slot?.returnOrderNumber,
      status,
      statusText:
        RETURN_ORDER_ITEM_STATUS?.[status]?.label ||
        ORDER_STATUS?.[status]?.text ||
        humanizeEnum(status),
      statusColor: ORDER_STATUS?.[status]?.color || RETURN_STATUS_COLOR,
      fallbackText: 'Return placed',
    });
  }
  return rows;
};

const SlotOrderFooter = ({ rows = [] }) => (
  <div className="roadmap-slot-footer is-order">
    {rows?.map((row) => (
      <div key={row?.key} className={`roadmap-slot-order-row is-${row?.key}`}>
        <div className="roadmap-slot-order-number">
          {row?.number ? (
            <Text copyable={{ text: row?.number }}>{row?.number}</Text>
          ) : (
            <Text type="secondary">{row?.fallbackText}</Text>
          )}
        </div>
        {row?.status && (
          <span className="roadmap-slot-order-status" title={row?.statusText}>
            <Badge color={row?.statusColor} text={row?.statusText} />
          </span>
        )}
      </div>
    ))}
  </div>
);

const SlotActionsFooter = ({ onSwap, onEdit, disabled, lockReason }) => (
  <div className="roadmap-slot-footer is-actions">
    {onSwap && (
      <Tooltip
        title={lockReason || 'Swap with another week or a user suggestion'}
      >
        <Button
          type="text"
          size="small"
          icon={<SwapOutlined />}
          onClick={onSwap}
          disabled={disabled}
        >
          Swap
        </Button>
      </Tooltip>
    )}
    {onEdit && (
      <Tooltip title={lockReason || 'Pick a different toy'}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={onEdit}
          disabled={disabled}
        >
          Change
        </Button>
      </Tooltip>
    )}
  </div>
);

const RoadmapSlotTile = ({
  slotConfig,
  toy,
  slot,
  onEdit,
  onSwap,
  disabled,
  lockReason,
  label,
}) => {
  const slotLabel = label || slotConfig?.label;

  if (!toy) {
    return (
      <Tooltip title={lockReason}>
        <button
          type="button"
          className="roadmap-slot-tile is-empty"
          onClick={onEdit}
          disabled={disabled || !onEdit}
        >
          <span className="roadmap-slot-empty-body">
            <span className="roadmap-slot-empty-icon">
              <PlusOutlined />
            </span>
            <span>{onEdit ? 'Assign Toy' : 'Not assigned'}</span>
          </span>
        </button>
      </Tooltip>
    );
  }

  const isOrdered = isSlotOrdered(slot);
  const orderRows = getSlotOrderRows(slot);
  const hasActions = !isOrdered && (onEdit || onSwap);
  return (
    <div className={`roadmap-slot-tile ${isOrdered ? 'is-ordered' : ''}`}>
      <div className="roadmap-slot-tile-body">
        <ToyThumbnail toy={toy} size={76} />
        <div className="roadmap-slot-tile-info">
          <Tag color={slotConfig?.color}>{slotLabel}</Tag>
          <Paragraph
            strong
            className="roadmap-slot-toy-name"
            ellipsis={{ rows: 2, tooltip: toy?.name }}
          >
            {toy?.name || '-'}
          </Paragraph>
          <div className="roadmap-slot-toy-meta">
            <Text
              type="secondary"
              className="ultra-plan-caption"
              copyable={!!toy?.toyNumber && { text: toy?.toyNumber }}
            >
              {`#${toy?.toyNumber || '-'}`}
            </Text>
            <ToyAgeTags toy={toy} />
          </div>
        </div>
      </div>
      {orderRows?.length > 0 && <SlotOrderFooter rows={orderRows} />}
      {hasActions && (
        <SlotActionsFooter
          onSwap={onSwap}
          onEdit={onEdit}
          disabled={disabled}
          lockReason={lockReason}
        />
      )}
    </div>
  );
};

export default RoadmapSlotTile;
